import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import _ from 'lodash'
import {Avatar} from 'antd'
import {
    BsBookHalf,
    BsCameraVideoFill,
    BsLightningChargeFill,
    BsPaperclip,
    BsPersonBoundingBox,
    BsStickyFill,
} from 'react-icons/bs'
import getAvatar from '../../../../../helpers/getAvatar'
import {PiCalendarSlashDuotone, PiExamFill} from 'react-icons/pi'
import {BiTime} from 'react-icons/bi'
import {Link} from 'react-router-dom'
import {getId} from 'apis/model/base'
import getTitle from 'helpers/getTitle'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {
    course_class_getAbsenceRequestsByStudent_Api,
    course_class_getAttendanceDetailsByAuth_Api,
    course_class_getGradeDetailsByAuth_Api,
    timetable_getMilestonesByCourseClass_Api,
} from 'apis'
import {getResponseItem, getResponseItems} from 'views/Shared'
import {bindQueryParam} from '../../../functions/routerHelper'
import CourseClassContext from '../../CourseClassContext'
import {ATTENDANCE_STATUS_LABEL, getStatusConfig} from '../CourseClassAttendanceForm'
import {useCourseAttendances} from '../../functions/useCourseAttendances'
import {useCourseClassAbsenceRequestForm} from '../../functions/useCourseClassAbsenceRequestForm'
import {AttendanceActionTypes, course_delivery_mode_options} from '../../index'
import EmptyHolder from '../../../../../components/EmptyHolder'
import {formatSimplifyDate} from "../../../../../helpers/dateUtils";
import {formatDate} from "../../../../../helpers/datetimeHelpers";
import SessionMaterialsDrawer from '../../../Timetable/SessionMaterialsDrawer'
import SessionNotesDrawer from '../../../Timetable/SessionNotesDrawer'
import SessionMeetingLinkDrawer from '../../../Timetable/SessionMeetingLinkDrawer'
import {flattenTimetable} from '../../../../Workspace/Course/functions/timetableHelpers'
import {PastSessionsSummary} from '../../../../Workspace/Course/components/PastSessionsSummary'
import {SessionPrivateNote, useMySessionNotes} from '../components/MyClassNotes'

// Pane "Sắp tới" của CourseSchedulePanel (nhúng khi có course_class_id - xem CourseSchedulePanel.js,
// trước đây là 1 sub-tab riêng "Điểm danh của tôi" cạnh "Lịch học", đã gộp vào đây để tránh trùng nội
// dung) — hiển thị TOÀN BỘ buổi học đã lên lịch của lớp (giống tab "Chi tiết" cũ đã bỏ, cùng nguồn
// timetable_getMilestonesByCourseClass_Api), KHÔNG chỉ những buổi đã có bản ghi điểm danh
// - vì lịch còn cả buổi TƯƠNG LAI chưa điểm danh. Với mỗi buổi, đối chiếu qua
// course_class_getAttendanceDetailsByAuth_Api (đúng API đã dùng ở view học viên cũ) để lấy trạng
// thái điểm danh CỦA CHÍNH mình cho buổi đó, khớp qua attendance.course_class_timetable_detail.id -
// buổi chưa có bản ghi (thường là tương lai) hiện badge "Sắp diễn ra" thay vì bịa trạng thái.
// Auto-scroll tới buổi gần nhất chưa kết thúc khi tab mở lên (đang diễn ra hoặc sắp diễn ra).
//
// "Tài liệu buổi học" lấy từ field thật TimetableDetailTransformer.materials (admin/GV lớp thêm qua
// CourseClassTimetablePanel.js - trang quản trị riêng, HOẶC sửa tại chỗ ngay ở đây qua
// SessionMaterialsDrawer.js nếu người xem có quyền sửa lớp này - can_edit = item.edit, đúng flag đã
// dùng ở CourseClassTimetablePanel.js). Học viên (không có quyền sửa) chỉ thấy list link readonly,
// và nếu buổi đó KHÔNG có tài liệu/ghi chú/link phòng học thì ẨN LUÔN khối đó (không hiện khối rỗng).
// "Ghi chú" (link trước buổi học/bài tập về nhà/lưu ý khác) dùng đúng field thật
// TimetableDetail.notes (đã có sẵn, KHÔNG nằm trong materials) - sửa tại chỗ qua SessionNotesDrawer.js
// cùng cơ chế quyền với materials ở trên. flattenTimetable() PHẢI giữ nguyên notes của từng detail,
// không lấy nhầm notes cấp milestone (lịch trình cha) - trước đây có bug ghi đè sai chỗ này.
// Nút "Xin phép vắng mặt" CHƯA gắn - cần resolve đúng student_id trong lớp này trước (bàn lượt khác).
//
// "Link phòng học online" (showMeetingLink) CHỈ hiện khi item.course.delivery_mode là online/hybrid
// (Course::DELIVERY_MODE_*, field thật - đã bổ sung ở CourseTransformerTiny.php vì item.course được
// nest qua transformer này) - lớp offline thuần không hiện khối này. 3 khối tài liệu/ghi chú/link
// phòng học LUÔN xếp CHỒNG (flex-col), KHÔNG chia 2 cột cho buổi isUpcoming như trước nữa (báo lỗi:
// chia đôi chiều rộng nhiều khi không đủ chỗ hiển thị nội dung dài).
//
// "Tạo điểm danh" - VẼ nút khi ĐỦ 3 điều kiện: canEdit (GV/admin, cùng flag mọi nút sửa khác ở trên),
// buổi CHƯA có bản ghi điểm danh (status == null - có rồi thì sửa qua tab "Điểm danh" riêng, tránh tạo
// trùng), không phải ngày lễ. NGÀY học đã tới hay chưa (hasArrived, so start_time với "đầu ngày hôm
// nay" theo giờ máy người xem) KHÔNG còn quyết định có vẽ nút hay không - trước đây buổi tương lai ẩn
// hẳn nút, GV không biết tính năng này tồn tại (bạn báo) - giờ buổi tương lai vẫn thấy nút, nhưng
// disabled + màu secondary (nhạt), tới đúng ngày mới enable + primary (indigo đậm) như cũ. dùng
// .startOf('day') thay vì so thẳng instant để buổi ĐANG diễn ra hôm nay vẫn bấm được ngay từ đầu buổi
// chứ không phải đợi qua giờ start_time; so theo NGÀY lịch nên không lệch dù giờ học lớn hơn giờ hiện
// tại. Tái dùng NGUYÊN useCourseAttendances + AttendanceActionTypes.CREATE đã có ở
// CourseClassAttendanceTab.js (không tự chế flow điểm danh song song) - prefill sẵn subject/buổi/ngày
// của ĐÚNG session này qua course_class_timetable_detail(_id), khỏi phải chọn lại thủ công như khi
// bấm "Tạo" từ tab Điểm danh gốc. Tạo xong hook tự mở form điểm danh chi tiết (đánh dấu từng học
// viên) - hành vi có sẵn của useCourseAttendances, không cần code thêm ở đây.

// flattenTimetable: đã tách sang views/Workspace/Course/functions/timetableHelpers.js để
// dùng chung với CourseClassTimetablePanel.js (tab "Theo dòng thời gian").

// Buổi CHƯA có bản ghi điểm danh (status=null) vẫn phải có badge, không riêng gì buổi gần nhất -
// phân biệt "còn ở tương lai" (Sắp diễn ra) khỏi "đã qua nhưng chưa được điểm danh" (Chưa điểm danh,
// dùng lại đúng label/màu đã có ở CourseClassAttendanceForm.js, không tự chế nhãn mới).
const SessionStatusBadge = ({status, isFuture}) => {
    const t = useTranslate()
    if (status == null) {
        return isFuture ? (
            <span className="px-2.5 py-0.5 rounded-full text-xs italic font-medium bg-blue-50 text-blue-600 border border-blue-400 flex-shrink-0 whitespace-no-wrap">
                {t('upcoming')}
            </span>
        ) : (
            <span className="px-2.5 py-0.5 rounded-full text-2xs italic font-medium background-100 text-color-300 border border-color-100 flex-shrink-0 whitespace-no-wrap">
                {t('not check in')}
            </span>
        )
    }
    const {color} = getStatusConfig(status)
    const label = ATTENDANCE_STATUS_LABEL[status] ?? 'undefined'
    return (
        <span className={classNames('px-2.5 py-1 rounded-full text-2xs font-semibold border flex-shrink-0 whitespace-no-wrap', color)}>
            {t(label)}
        </span>
    )
}

// Nút tròn nhỏ (icon-only, "dot") thay cho 1 khung rỗng đầy đủ - dùng làm trigger gọn cho
// SessionMaterialsDrawer/SessionNotesDrawer/SessionMeetingLinkDrawer khi mục đó CHƯA có dữ liệu (xem
// hàng "add session info hint" bên dưới) - bạn báo 3 khung rỗng xếp chồng chiếm quá nhiều chỗ dọc
// trên mobile lúc chưa nhập gì, gộp lại còn 1 hàng icon nhỏ.
// PHẢI nhận ...props và gắn vào <span> - DrawDialog dùng cloneElement(component, {onClick: ...}) để
// gắn hành vi mở drawer LÊN THẲNG phần tử được truyền vào `component`; component đó là chính
// CompactAddTrigger (không phải <span> bên trong), nên onClick được clone vào PROP của
// CompactAddTrigger chứ không tự động chạy xuống DOM - thiếu {...props} thì onClick bị nuốt mất,
// bấm vào không có phản ứng gì (bạn báo lỗi này - nút chỉ để "làm cảnh").
const CompactAddTrigger = ({icon: Icon, label, ...props}) => (
    <span
        title={label}
        {...props}
        className="w-7 h-7 flex flex-center rounded-full background-100 hover:bg-indigo-100 text-color-400 hover:text-indigo-600 cursor-pointer flex-shrink-0 transition-colors">
        <Icon size={13}/>
    </span>
)

// Block "hôm nay có bài thi" - CHỈ hiện trên đúng buổi (SessionCard) trùng NGÀY với grade_date của 1
// cột điểm đang gắn course_class_exam active (status=1) mà CHÍNH học viên đang xem CHƯA có điểm (xem
// examByDate ở MyClassSessionsPanel - so khớp theo ngày, KHÔNG theo subject/session cụ thể vì
// course_class_exam không có field liên kết session). Nền gradient tím-chàm + bố cục giống HỆT card
// "exam available" đã có sẵn ở MyBoard (CourseClassGradeTab.js, tab "Điểm số") - tái dùng đúng ngôn
// ngữ hình ảnh cho 1 khái niệm ở 2 nơi, không tự chế màu/layout riêng.
const SessionExamBanner = ({examInfo}) => {
    const t = useTranslate()
    if (!examInfo) return null
    const {grade, exam} = examInfo

    return (
        <div className="p-3 flex items-center gap-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-500 shadow-md">
            <div className="w-10 h-10 flex-shrink-0 flex flex-center bg-white-20 rounded-xl">
                <PiExamFill size={20} className="text-white"/>
            </div>
            <div className="flex-1 min-w-0">
                <p className="mb-1 px-2 py-0.5 w-fit bg-white-20 rounded-md font-bold text-2xs text-white-80 uppercase tracking-wide">
                    {t('exam today')}
                </p>
                <p className="font-bold text-sm text-white leading-tight truncate">
                    {getTitle(grade)}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                    {exam?.duration_minutes > 0 && (
                        <span className="flex items-center gap-1 font-medium text-2xs text-white-70">
                            <BiTime size={11}/>
                            {exam.duration_minutes} {t('minutes')}
                        </span>
                    )}
                    {exam?.max_score > 0 && (
                        <span className="font-medium text-2xs text-white-70">
                            {exam.max_score} {t('points')}
                        </span>
                    )}
                    {grade?.score_weight > 0 && (
                        <span className="font-medium text-2xs text-white-70">
                            {t('score_weight')}: {grade.score_weight}
                        </span>
                    )}
                </div>
            </div>
            <Link
                to={'/course-class-exam/' + exam?.id}
                onClick={(e) => e.stopPropagation()}
                className="px-3 py-1.5 flex items-center gap-1 flex-shrink-0 bg-white hover:bg-white-90 text-indigo-600 rounded-lg text-xs font-bold no-underline">
                {t('Take exam')}
            </Link>
        </div>
    )
}

// Buổi ĐÃ diễn ra (end_time đã qua) hiện gọn + nhạt màu hơn hẳn buổi hiện tại/sắp tới - phân biệt rõ
// "đã học xong" khỏi phần lịch còn phải quan tâm, đồng thời đỡ rợp mắt khi lớp đã học nhiều buổi.
const SessionCard = React.forwardRef(({
    session, index, isUpcoming, isPast, canEdit, canManageAttendance, showMeetingLink, privateNote, onRefresh,
    onCreateAttendance, onEditAttendance, canRequestAbsence, absenceRequest, onRequestAbsence, onCancelAbsence,
    examInfo,
}, ref) => {
    const t = useTranslate()
    const {start_time, end_time, location_address, title, notes, status, has_attendance_sheet, is_holiday, materials = [], teacher, subject, meeting_url} = session ?? {}
    const date = start_time ? dayjs(start_time) : null
    // Lịch trình (Timetable.title) đôi khi chưa được đặt tên riêng (rỗng) - trước đây rơi về chữ
    // chung chung "Buổi" trùng với "Buổi N" ở dòng trên, không phân biệt được buổi nào - fallback về
    // NGÀY học cụ thể (dữ liệu thật, luôn có) thay vì bịa chữ, đỡ vô nghĩa hơn.
    const fallbackTitle = formatSimplifyDate(start_time, '', 'dddd, DD MMMM, YYYY')
    const displayTitle = is_holiday === 1 ? t('holiday') : (title || fallbackTitle || t('session'))

    const timeDetail = dayjs(start_time).format('HH:mm') !== dayjs(end_time).format('HH:mm') ?
        `${formatDate(start_time, 'HH:mm')} – ${formatDate(end_time, 'HH:mm')}` :
        formatDate(start_time, 'HH:mm')

    // So theo NGÀY lịch (startOf('day')), không so thẳng instant start_time - buổi học ĐANG diễn ra
    // hôm nay (chưa tới giờ end_time) vẫn phải bấm được ngay, không đợi qua giờ học mới hiện nút.
    const hasArrived = !!start_time && !dayjs(start_time).startOf('day').isAfter(dayjs().startOf('day'))
    // Trước đây hasArrived nằm trong điều kiện HIỆN nút - buổi tương lai không thấy nút đâu cả, không
    // biết tính năng này tồn tại (bạn báo). Giờ tách riêng: showAttendanceAction quyết định có VẼ nút
    // hay không (canEdit + không phải ngày lễ, KHÔNG còn phụ thuộc status - buổi CHƯA điểm danh vẫn vẽ
    // nút Tạo (tạm khoá tới ngày), buổi ĐÃ điểm danh chuyển hẳn sang nút Sửa, luôn bấm được ngay - GV
    // báo cần "thêm và sửa điểm danh" ở đúng đây, không phải sang tab riêng nữa).
    // has_attendance_sheet - field thật mới từ BE (TimetableController::getMilestonesByCourseClass,
    // opt-in "attendances" trên timetable_detail), CHỈ trả cho admin/GV/lớp trưởng - biết buổi này ĐÃ
    // có mục điểm danh do ai đó tạo hay chưa, KHÔNG phụ thuộc status (status = trạng thái điểm danh
    // CỦA CHÍNH người xem, admin/GV thường không phải học viên lớp nên status luôn null dù buổi đã
    // điểm danh - trước đây dùng status != null làm hasAttendance nên nút Tạo/Sửa sai với đúng nhóm
    // cần nhất, bạn báo lỗi này). Học viên thường không có has_attendance_sheet (BE không trả) ->
    // fallback về status != null như cũ, đủ dùng cho showRequestAbsence bên dưới.
    const hasAttendance = has_attendance_sheet ?? (status != null)
    // canManageAttendance - CỜ RIÊNG cho nút điểm danh (khác canEdit dùng cho tài liệu/ghi chú/link
    // phòng học): tái dùng ĐÚNG item.available_actions.can_checkin có sẵn (CourseClassTransformer::
    // canCheckin - admin/coordinator/giáo viên lớp/lớp trưởng), KHÔNG tự chế điều kiện mới - bạn báo
    // nút điểm danh phải hiện thêm cho GV thường + lớp trưởng, 2 nhóm canEdit (admin/coordinator only)
    // không có.
    const showAttendanceAction = canManageAttendance && is_holiday !== 1
    // Thêm tư liệu / ghi chú (nhắc nhở) / link phòng cho buổi học - trước chỉ canEdit (admin/
    // coordinator). Giờ cho luôn nhóm "quản lý lớp" (canManageAttendance = admin/coordinator/GV lớp/
    // lớp trưởng) - lớp trưởng đăng link bài tập / nhắc nhở cho lớp là việc bình thường. BE:
    // TimetableController::canManageTimetableDetail đã mở đúng nhóm này (KHÔNG mở sửa/xóa cả TKB).
    const canManageSession = canEdit || canManageAttendance
    // "Xin phép nghỉ học" - action RIÊNG của học viên (canRequestAbsence = có student_id của CHÍNH
    // người xem trong lớp này, xem MyClassSessionsPanel - độc lập với canEdit, 1 người có thể vừa là
    // GV vừa có con là học viên lớp khác nên 2 nút này không loại trừ nhau). Không cần chờ hasArrived
    // như điểm danh - xin nghỉ càng sớm càng tốt, kể cả buổi còn xa. Ẩn hẳn nếu buổi ĐÃ có điểm danh
    // (status != null - xin nghỉ lúc này vô nghĩa, nên gặp trực tiếp GV/admin sửa lại điểm danh).
    // !isPast: buổi ĐÃ qua KHÔNG cho xin phép nghỉ (xin nghỉ buổi đã học xong là vô nghĩa) - bạn chốt bỏ.
    const showRequestAbsence = canRequestAbsence && is_holiday !== 1 && !hasAttendance && !isPast

    // Chỉ tính cho buổi SẮP TỚI gần nhất (ribbon/dòng đếm phút) - đều suy thật từ start_time/end_time,
    // không bịa "cửa sổ mở phòng" hay đề cương (không có dữ liệu/tính năng tương ứng ở BE).
    const isToday = !!date && date.isSame(dayjs(), 'day')
    const isHappeningNow = !!start_time && !!end_time && dayjs().isAfter(dayjs(start_time)) && dayjs().isBefore(dayjs(end_time))
    const minutesUntilStart = start_time ? dayjs(start_time).diff(dayjs(), 'minute') : null
    const isStartingSoon = !isHappeningNow && minutesUntilStart != null && minutesUntilStart >= 0 && minutesUntilStart <= 60

    // 3 mục tài liệu/ghi chú/link phòng học - tính sẵn "có dữ liệu chưa" 1 lần, dùng cho cả khối
    // đầy đủ (hasX) lẫn hàng gộp icon nhỏ "chưa có" (!hasX, xem CompactAddTrigger).
    const hasMaterials = materials.length > 0
    const hasNotes = !!notes
    const hasMeetingUrl = !!meeting_url

    // Khối hiển thị tư liệu / ghi chú / link phòng học ĐÃ có dữ liệu (readonly cho học viên, kèm nút
    // sửa cho canEdit) - CHIA SẺ giữa buổi đã qua lẫn buổi hiện tại/sắp tới, không dựng 2 bản song
    // song. Mục nào CHƯA có dữ liệu thì ẨN ở đây (nút thêm nằm ở actionRow bên dưới).
    const infoBlocks = is_holiday !== 1 && (hasMaterials || hasNotes || (showMeetingLink && hasMeetingUrl)) ? (
        <div className="flex flex-col gap-2">
            {hasMaterials && (
                <div className="p-2.5 background-50 rounded-xl flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <BsPaperclip size={12} className="text-color-400 flex-shrink-0"/>
                        <span className="text-xs font-semibold text-color-300">{t('session materials')}</span>
                        {canEdit && (
                            <SessionMaterialsDrawer session={session} onRefresh={onRefresh}/>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {materials.map((material) => (
                            <a
                                key={material.id}
                                href={material.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-2 py-0.5 flex items-center gap-1 rounded-full text-2xs font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100 no-underline truncate max-w-full">
                                <BsPaperclip size={11} className="flex-shrink-0"/>
                                <span className="truncate">{material.title || material.url}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}
            {hasNotes && (
                <div className="p-2.5 background-50 rounded-xl flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <BsStickyFill size={12} className="text-color-400 flex-shrink-0"/>
                        <span className="text-xs font-semibold text-color-300">{t('session notes')}</span>
                        {canEdit && (
                            <SessionNotesDrawer session={session} onRefresh={onRefresh}/>
                        )}
                    </div>
                    <span className="text-xs text-color-300 italic leading-snug whitespace-pre-line break-words">{notes}</span>
                </div>
            )}
            {/* Link phòng học online - CHỈ hiện khi khóa có phần online/hybrid (showMeetingLink),
                sửa qua SessionMeetingLinkDrawer (canEdit) - mirror ĐÚNG khối tài liệu/ghi chú ở trên. */}
            {showMeetingLink && hasMeetingUrl && (
                <div className="p-2.5 background-50 rounded-xl flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <BsCameraVideoFill size={12} className="text-color-400 flex-shrink-0"/>
                        <span className="text-xs font-semibold text-color-300">{t('session meeting link')}</span>
                        {canEdit && (
                            <SessionMeetingLinkDrawer session={session} onRefresh={onRefresh}/>
                        )}
                    </div>
                    <a
                        href={meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="w-fit px-2.5 py-1 flex items-center gap-1.5 rounded-full text-2xs font-bold text-white bg-blue-600 hover:bg-blue-700 no-underline">
                        <BsCameraVideoFill size={11}/>
                        {t('join online room')}
                    </a>
                </div>
            )}
        </div>
    ) : null

    // Hàng nút hành động (icon thêm tư liệu/ghi chú/link cho mục CHƯA có + Tạo/Điểm danh + Xin nghỉ) -
    // CHIA SẺ giữa buổi đã qua lẫn buổi hiện tại/sắp tới. Buổi đã qua: showRequestAbsence luôn false
    // (xem trên) nên chỉ còn icon thêm tư liệu + nút điểm danh; hasArrived luôn true nên nút "Tạo điểm
    // danh" enable ngay - GV bổ sung điểm danh cho buổi đã học mà lúc đó quên chưa tạo.
    const showAddSessionInfo = canManageSession && is_holiday !== 1 && (!hasMaterials || !hasNotes || (showMeetingLink && !hasMeetingUrl))
    const actionRow = (showAddSessionInfo || showAttendanceAction || showRequestAbsence) ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
            {showAddSessionInfo && (
                <div className="flex items-center gap-1.5">
                    {/* Nhãn CHỈ hiện từ sm+ (đủ chỗ) - mobile chỉ còn icon, đỡ chật khi đứng chung hàng
                        với nút Tạo/Điểm danh + Xin phép nghỉ. */}
                    <span className="hidden sm:inline text-2xs text-color-300 italic flex-shrink-0">{t('add session info hint')}</span>
                    {!hasMaterials && (
                        <SessionMaterialsDrawer
                            session={session} onRefresh={onRefresh}
                            trigger={<CompactAddTrigger icon={BsPaperclip} label={t('session materials')}/>}
                        />
                    )}
                    {!hasNotes && (
                        <SessionNotesDrawer
                            session={session} onRefresh={onRefresh}
                            trigger={<CompactAddTrigger icon={BsStickyFill} label={t('session notes')}/>}
                        />
                    )}
                    {showMeetingLink && !hasMeetingUrl && (
                        <SessionMeetingLinkDrawer
                            session={session} onRefresh={onRefresh}
                            trigger={<CompactAddTrigger icon={BsCameraVideoFill} label={t('session meeting link')}/>}
                        />
                    )}
                </div>
            )}
            {/* Buổi CHƯA điểm danh -> nút Tạo (tạm khoá tới ngày, xem hasArrived). Buổi ĐÃ có điểm danh
                -> nút Điểm danh, mở THẲNG màn hình checklist học viên, luôn bấm được ngay. */}
            {showAttendanceAction && (
                hasAttendance ? (
                    <button
                        type="button"
                        onClick={() => onEditAttendance(session)}
                        className={classNames(
                            "px-3 py-2 flex flex-center gap-1.5 rounded-xl text-xs  cursor-pointer transition-colors",
                            isPast ? 'font-semibold text-secondary-600 bg-secondary-50 hover:bg-secondary-100' : 'font-bold text-white bg-indigo-600 hover:bg-indigo-700'
                        )}>
                        <BsPersonBoundingBox size={13}/>
                        {t('attendance')}
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled={!hasArrived}
                        onClick={() => hasArrived && onCreateAttendance(session)}
                        title={!hasArrived ? t('create attendance available on session day') : undefined}
                        className={classNames(
                            'px-3 py-2 flex flex-center gap-1.5 rounded-xl text-xs font-bold border-0 transition-colors',
                            hasArrived
                                ? isPast ? 'font-semibold text-secondary-600 bg-secondary-50 hover:bg-secondary-100'
                                : 'text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer'
                                : 'background-100 text-color-300 cursor-not-allowed'
                        )}>
                        <BsPersonBoundingBox size={13}/>
                        {t('create attendance for session')}
                    </button>
                )
            )}
            {(showRequestAbsence && !isPast) && (
                absenceRequest ? (
                    <div className="px-3 py-2 flex items-center gap-2 rounded-xl text-xs font-bold background-100 text-color-300 border border-dashed border-color-200">
                        <PiCalendarSlashDuotone size={14}/>
                        {t('absence request submitted')}
                        <button
                            type="button"
                            onClick={() => onCancelAbsence(absenceRequest)}
                            className="text-rose-500 hover:text-rose-600 underline border-0 bg-transparent cursor-pointer p-0">
                            {t('cancel')}
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => onRequestAbsence(session)}
                        className="px-3 py-2 flex flex-center gap-1.5 rounded-xl text-xs font-bold border border-color-200 text-color-300 hover:border-amber-300 hover:text-amber-700 cursor-pointer transition-colors">
                        <PiCalendarSlashDuotone size={14}/>
                        {t('request absence for session')}
                    </button>
                )
            )}
        </div>
    ) : null

    if (isPast) {
        const {end_time} = session ?? {}
        return (
            <div ref={ref} className="px-3 py-2 flex items-start gap-3">
                <div className="flex flex-col flex-1 min-w-0 gap-1">
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="flex flex-col flex-1 min-w-0 gap-1">
                            <span className="font-medium text-gray-600 tracking-wide truncate capitalize">
                                {t('session')} {index + 1}{': '}
                                {displayTitle}
                            </span>
                            {date && (
                                <span className="font-medium text-xs text-color-500 tracking-wider">
                                {date.format('HH:mm')}{end_time && ` - ${dayjs(end_time).format('HH:mm')}`}
                            </span>
                            )}
                        </div>
                        <SessionStatusBadge status={status} isFuture={false}/>
                    </div>
                    {is_holiday !== 1 && (
                        <SessionPrivateNote timetable_detail_id={session?.id} initialNote={privateNote}/>
                    )}
                    {/* Buổi đã qua vẫn hiện tư liệu/ghi chú đã có + hàng nút (thêm tư liệu, điểm danh) -
                        đồng bộ với card "sắp diễn ra". KHÔNG có ribbon/exam banner/xin nghỉ. */}
                    {infoBlocks}
                    {actionRow}
                </div>
            </div>
        )
    }

    return (
        <div
            ref={ref}
            className={classNames(
                // px-3 (thay p-4 chung) - mobile bớt 1 tầng padding ngang, card này giờ là khung DUY
                // NHẤT còn lại quanh 1 buổi học (card ngoài CourseSchedulePanel.js đã bỏ padding/viền
                // trên mobile) nên không cần siết quá chặt nữa, nội dung có thêm chỗ thở.
                'relative overflow-hidden px-3 py-4 sm:p-4 flex flex-col gap-2 background rounded-2xl transition-shadow',
                isUpcoming ? 'border-2 border-blue-400 bg-blue-50 shadow-md' : 'border border-color-100 shadow-sm '
            )}>
            {/* Ribbon "SẮP DIỄN RA" (+ "HÔM NAY" nếu đúng ngày hôm nay) thay cho pill trạng thái ở
                buổi SẮP TỚI gần nhất - buổi tương lai khác vẫn giữ nguyên pill SessionStatusBadge cũ. */}
            {isUpcoming && (
                <div className="absolute top-0 right-0 px-3 py-1 flex items-center gap-1 rounded-bl-2xl text-2xs font-bold text-white bg-blue-600 uppercase tracking-wide">
                    <BsLightningChargeFill size={10}/>
                    {isHappeningNow ? t('happening now') : t('upcoming session ribbon')}
                    {isToday && ` • ${t('today')}`}
                </div>
            )}
            <div className="flex items-start justify-between gap-2 pr-1">
                <span className={classNames("text-2xs font-bold uppercase tracking-wider", isUpcoming ? 'text-blue-600' : 'text-gray-600')}>
                    {t('session')} {index + 1}{date && ` - ${date.format('DD/MM/YYYY')}`}
                </span>
                {!isUpcoming && <SessionStatusBadge status={status} isFuture={true}/>}
            </div>
            <div className="flex flex-col gap-1">
                <div className={classNames("font-bold leading-snug break-words", isUpcoming ? 'text-lg text-blue-700' : 'text-base text-color-000')}>
                    {displayTitle}
                </div>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <span className={classNames("font-semibold tracking-wide truncate capitalize text-xs", isUpcoming ? 'text-gray-600' : 'text-color-300')}>
                        {timeDetail} {location_address && ` - ${location_address}`}
                    </span>
                    {isStartingSoon && (
                        <span className="px-1.5 py-0.5 rounded-full text-2xs font-semibold bg-orange-50 text-orange-600 whitespace-no-wrap">
                            {t('starting in n minutes', {n: minutesUntilStart})}
                        </span>
                    )}
                    {/* Pill "Học trực tuyến" trước đây đứng ở đây TRÙNG Ý với khối "Phòng học online"
                        (+ nút "Vào phòng học online") ngay bên dưới - cả 2 cùng báo "buổi này online"
                        (bạn báo lỗi trùng) - bỏ pill, giữ đúng 1 chỗ báo + có action thật (khối bên
                        dưới). */}
                </div>
                {/* Thông tin phụ (môn học/giảng viên) - field thật lấy từ Timetable cha (xem
                    flattenTimetable), CHỈ hiện khi có (không bịa khi thiếu). flex-wrap để không vỡ
                    layout trên mobile khi tên môn/giảng viên dài. */}
                {(subject?.title || teacher?.fullname) && (
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-color-300">
                        {/* Icon môn học (sách) - trước đây KHÔNG có icon riêng, dễ nhầm với avatar
                            giảng viên ngay sau nó (bạn báo 2 cái đứng cạnh nhau nhìn như cùng 1 thứ). */}
                        {subject?.title && (
                            <BsBookHalf size={11} className="text-color-400 flex-shrink-0"/>
                        )}
                        {subject?.title && <span className="truncate">{getTitle(subject)}</span>}
                        {subject?.title && teacher?.fullname && <span className="text-color-100">•</span>}
                        {/* Avatar THẬT của giảng viên (teacher.avatar, không có thì tới user.avatar -
                            cùng cách TeacherRow đang làm) - trước đây chỉ có vòng tròn chữ cái đầu tên
                            đặt TRƯỚC tên môn học nên trông như icon của môn học, không phải giảng viên
                            (bạn báo nhầm lẫn này). Giờ đặt ĐÚNG cạnh chữ "Giảng viên:" + luôn hiện khi
                            có teacher (bỏ điều kiện isUpcoming, đồng nhất mọi buổi học). */}
                        {teacher?.fullname && (
                            <span className="flex items-center gap-1 truncate">
                                <Avatar
                                    size={16}
                                    src={teacher?.avatar || getAvatar(teacher?.user)}
                                    className="flex-shrink-0 flex flex-center background-200 text-2xs font-bold text-indigo-700 uppercase"
                                >
                                    {getTitle(teacher).trim().charAt(0)}
                                </Avatar>
                                <span className="truncate">{t('teacher')}: {getTitle(teacher)}</span>
                            </span>
                        )}
                    </div>
                )}
            </div>
            {/* Khối tư liệu/ghi chú/link phòng học + hàng nút hành động - JSX dùng chung, khai báo ở
                đầu SessionCard (infoBlocks / actionRow), buổi đã qua tái dùng đúng 2 khối này. */}
            {infoBlocks}
            {/* "Hôm nay có bài thi" - đứng TRÊN khối Ghi chú riêng (bạn yêu cầu tách biệt bằng nền
                khác), chỉ tính cho buổi is_holiday !== 1 giống mọi khối khác ở đây. */}
            {is_holiday !== 1 && <SessionExamBanner examInfo={examInfo}/>}
            {/* Ghi chú RIÊNG của user cho buổi học này - luôn hiện (mọi thành viên đều ghi được),
                độc lập với khối "Ghi chú buổi học" dùng chung ở trên. */}
            {is_holiday !== 1 && (
                <SessionPrivateNote timetable_detail_id={session?.id} initialNote={privateNote}/>
            )}
            {actionRow}
        </div>
    )
})

// PastSessionsSummary: tách sang views/Workspace/Course/components/PastSessionsSummary.js để dùng
// chung với CourseClassTimetablePanel.js (tab "Theo khai báo" của admin/GV).

const MyClassSessionsPanel = () => {
    const t = useTranslate()
    const {item} = useContext(CourseClassContext)
    const course_class_id = getId(item)
    // Đúng flag quyền sửa lớp đã dùng ở CourseClassTimetablePanel.js (trang quản trị riêng) - GV/admin
    // của lớp này cũng sửa được link tài liệu tại chỗ ngay trong tab học viên, không bắt buộc phải
    // sang trang quản trị khác chỉ để thêm 1 link.
    const can_edit = !!item?.edit
    // Cờ RIÊNG cho nút điểm danh - tái dùng item.available_actions.can_checkin có sẵn
    // (CourseClassTransformer::canCheckin - admin/coordinator/GV lớp (qua TKB)/lớp trưởng), KHÔNG dùng
    // can_edit (chỉ admin/coordinator) - bạn báo nút "Tạo/Sửa điểm danh" phải hiện thêm cho GV thường
    // và lớp trưởng, trong khi tài liệu/ghi chú/link phòng học vẫn giữ nguyên quyền cũ (can_edit).
    const can_manage_attendance = !!item?.available_actions?.can_checkin
    // Link phòng học CHỈ có ý nghĩa khi khóa có phần online (delivery_mode - Course::DELIVERY_MODE_*,
    // 1=offline/2=online/3=hybrid, field thật trên item.course - xem CourseTransformerTiny.php) - lớp
    // offline thuần không hiện khối này, tránh gây hiểu nhầm là buổi nào cũng học online được.
    const isOnlineOrHybrid = [course_delivery_mode_options.online.value, course_delivery_mode_options.hybrid.value]
        .includes(item?.course?.delivery_mode)
    const upcomingRef = useRef(null)
    const hasScrolledRef = useRef(false)
    const [showPastSessions, setShowPastSessions] = useState(false)
    const [refreshKey, setRefreshKey] = useState(0)

    // Tái dùng NGUYÊN flow tạo điểm danh đã có (CourseClassAttendanceTab.js) - không tự chế modal/
    // API riêng. `modal` render ở cuối component; `handleCreateAttendance` build sẵn initialValues từ
    // ĐÚNG session được bấm (subject/buổi/ngày) để prefill form, đỡ phải chọn lại thủ công.
    const {modal: attendanceModal, handleAction: handleAttendanceAction} = useCourseAttendances({course_class: item})
    const handleCreateAttendance = (session) => {
        const {start_time, subject} = session ?? {}
        handleAttendanceAction(AttendanceActionTypes.CREATE, {
            subject_id: getId(subject),
            course_class_timetable_detail: {id: session?.id},
            course_class_timetable_detail_id: session?.id,
            attendance_date: dayjs(start_time),
            title: _.capitalize(dayjs(start_time).format('dddd, DD/MM/YYYY')),
        })
    }
    // Buổi ĐÃ có điểm danh -> mở THẲNG màn hình checklist học viên (AttendanceActionTypes.DETAIL) -
    // ĐÚNG màn hình tự mở ra ngay sau khi tạo điểm danh, để "vào điểm danh tiếp" (bạn báo bấm nút này
    // phải điểm danh tiếp được, không phải sửa metadata buổi) - tái dùng NGUYÊN
    // useCourseAttendances.js, không tự chế màn hình riêng. Sửa tựa đề/môn/ngày (UPDATE) giờ chuyển
    // vào nút "Sửa" trong header của chính màn hình checklist đó.
    const handleEditAttendance = (session) => {
        if (!session?.attendance_id) return
        handleAttendanceAction(AttendanceActionTypes.DETAIL, {id: session.attendance_id})
    }

    const {response: timetableResponse, isLoading: isTimetableLoading} = useAsync({
        apiInfo: timetable_getMilestonesByCourseClass_Api,
        query: bindQueryParam({id: course_class_id}),
        deps: [refreshKey],
    })
    const {response: attendanceResponse, isLoading: isAttendanceLoading} = useAsync({
        apiInfo: course_class_getAttendanceDetailsByAuth_Api,
        query: bindQueryParam({id: course_class_id}),
    })
    // Điểm/bài thi CỦA CHÍNH người xem - tái dùng NGUYÊN course_class_getGradeDetailsByAuth_Api (đã
    // dùng ở MyBoard, CourseClassGradeTab.js) để suy "hôm nay có bài thi" cho SessionExamBanner bên
    // dưới, KHÔNG tự chế API riêng chỉ để lấy lại đúng dữ liệu này.
    const {response: gradeDetailsResponse} = useAsync({
        apiInfo: course_class_getGradeDetailsByAuth_Api,
        query: bindQueryParam({id: course_class_id}),
    })
    // student_id CỦA CHÍNH người xem trong lớp này - BE đã resolve sẵn qua StudentUserLink ngay trong
    // API điểm danh ở trên (thêm field student_id vào response, xem AttendanceController.php), tận
    // dụng luôn thay vì gọi thêm API riêng chỉ để resolve lại đúng giá trị này. Không có student_id
    // (người xem không phải học viên lớp này, vd GV/admin thuần) -> không thấy nút "Xin phép nghỉ học"
    // (canRequestAbsence bên dưới).
    const myStudentId = _.get(getResponseItem(attendanceResponse), 'student_id')

    // Tái dùng NGUYÊN form/flow "Xin phép nghỉ học" đã có sẵn (CourseClassAbsenceRequestForm.js +
    // useCourseClassAbsenceRequestForm.js) - trước đây có đủ form/API nhưng CHƯA gắn vào đâu cả (đúng
    // như comment cũ ở đầu file), không tự chế modal/API song song. absence_type=1 (theo buổi cụ thể,
    // không phải khoảng ngày) + schedule_id=session.id prefill sẵn trong handleRequestAbsence bên dưới
    // - đúng buổi user bấm, khỏi phải chọn lại trong form.
    const {
        modal: absenceModal,
        handleAction: handleAbsenceAction,
        refresh_token: absenceRefreshToken,
    } = useCourseClassAbsenceRequestForm({
        student: myStudentId ? {id: myStudentId} : undefined,
        course_class_id,
    })
    const handleRequestAbsence = (session) => {
        handleAbsenceAction('CREATE_ABSENCE_REQUEST', {absence_type: 1, schedule_id: session?.id})
    }
    const handleCancelAbsence = (absenceRequest) => {
        handleAbsenceAction('CANCEL_ABSENCE_REQUEST', {id: absenceRequest?.id})
    }

    // Yêu cầu xin nghỉ CỦA CHÍNH mình trong lớp này - useAsyncAction (KHÔNG dùng useAsync) vì
    // myStudentId chỉ có SAU khi attendanceResponse tải xong (không có sẵn lúc mount) - useAsync tự
    // bắn ngay lúc mount bất kể id đã sẵn sàng chưa, từng gặp lỗi y hệt ở StudentPanels.js (xem
    // comment ở đó) nên áp dụng lại đúng cách né đã có: gọi thủ công qua effect, chỉ khi có
    // myStudentId. absenceRefreshToken đổi (tạo/huỷ xong) -> tự load lại danh sách.
    const {response: absenceRequestsResponse, handleAsyncAction: fetchAbsenceRequests} = useAsyncAction({
        apiInfo: course_class_getAbsenceRequestsByStudent_Api,
    })
    useEffect(() => {
        if (!myStudentId) return
        fetchAbsenceRequests({course_class_id}, {id: myStudentId})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myStudentId, course_class_id, absenceRefreshToken])

    // Ghi chú RIÊNG của user cho từng buổi - nạp 1 lượt, map theo timetable_detail_id.
    const {noteByDetailId} = useMySessionNotes(course_class_id)

    const isLoading = isTimetableLoading || isAttendanceLoading

    const sessions = useMemo(() => {
        const timetableData = _.get(getResponseItem(timetableResponse), 'data', [])
        const flattened = flattenTimetable(timetableData)

        // attendance_id (id của bản ghi Attendance cha, KHÁC attendance_detail.id) - cần cho nút "Sửa
        // điểm danh" (AttendanceActionTypes.UPDATE mở đúng form sửa qua id này, xem
        // useCourseAttendances.js).
        const statusByTimetableDetailId = {}
        const attendanceIdByTimetableDetailId = {}
        ;(getResponseItems(attendanceResponse) || []).forEach((a) => {
            const detailId = a?.attendance?.course_class_timetable_detail?.id
            if (detailId != null) {
                statusByTimetableDetailId[detailId] = a?.status
                attendanceIdByTimetableDetailId[detailId] = a?.attendance?.id
            }
        })

        return flattened.map((s) => {
            // s.attendances - field mới từ BE (timetable_detail.attendances, opt-in "attendances" ở
            // TimetableController::getMilestonesByCourseClass), CHỈ có khi người xem là admin/GV/lớp
            // trưởng (đúng nhóm cần nút Tạo/Sửa điểm danh) - mỗi buổi tối đa 1 mục điểm danh nên chỉ
            // cần phần tử đầu. Ưu tiên bản ghi CỦA CHÍNH người xem (attendanceIdByTimetableDetailId, có
            // sẵn nếu họ cũng là học viên lớp này) trước, fallback sang mục chung này - tránh 2 nguồn
            // đá nhau khi cả 2 đều có.
            const attendanceSheet = _.get(s, 'attendances[0]')
            return {
                ...s,
                status: statusByTimetableDetailId[s.id] ?? null,
                attendance_id: attendanceIdByTimetableDetailId[s.id] ?? attendanceSheet?.id ?? null,
                has_attendance_sheet: s.attendances != null
                    ? (attendanceIdByTimetableDetailId[s.id] != null || !!attendanceSheet)
                    : undefined,
            }
        })
    }, [timetableResponse, attendanceResponse])

    // Map session.id -> yêu cầu xin nghỉ ĐANG active (nếu có) - khớp theo 2 kiểu request đã hỗ trợ ở
    // form (CourseClassAbsenceRequestForm.js AbsenceTypeToggle): absence_type=1 khớp thẳng schedule_id,
    // absence_type=2 (theo khoảng ngày) khớp khi ngày buổi học nằm trong [from_date, to_date] - user có
    // thể đã xin nghỉ theo khoảng ngày TRƯỚC KHI bấm nút ở đây, vẫn phải nhận ra để không cho bấm xin
    // lại/hiện sai trạng thái.
    const absenceRequestBySessionId = useMemo(() => {
        const requests = getResponseItems(absenceRequestsResponse)
        const map = {}
        sessions.forEach((s) => {
            if (!s?.start_time) return
            const sessionDate = dayjs(s.start_time)
            const matched = requests.find((r) => (
                r.absence_type === 1
                    ? r.schedule_id === s.id
                    // isSameOrAfter/isSameOrBefore cần plugin dayjs riêng - dùng !isBefore/!isAfter
                    // (core, không cần extend) cho cùng ý nghĩa "trong khoảng, kể cả 2 đầu mút".
                    : !sessionDate.isBefore(dayjs(r.from_date), 'day') && !sessionDate.isAfter(dayjs(r.to_date), 'day')
            ))
            if (matched) map[s.id] = matched
        })
        return map
    }, [sessions, absenceRequestsResponse])

    // Map "ngày::subject_id" -> bài thi CHƯA làm, khớp SessionExamBanner ở SessionCard. Điều kiện ĐỦ
    // CẢ 4 (bạn yêu cầu khớp đúng buổi/môn/trạng thái/thời gian, không chỉ cùng ngày):
    // (1) cột điểm có gắn course_class_exam ĐANG active (status=1) - "trạng thái";
    // (2) có grade_date (ngày GIỜ làm bài đã khai) VÀ thời điểm hiện tại đã TỚI mốc đó (không phải
    //     chỉ cùng ngày lịch - buổi thi khai giờ chiều thì sáng cùng ngày chưa hiện nút) - "thời gian";
    // (3) so khớp CẢ subject_id (grade.subject) lẫn ngày với ĐÚNG session đang render - "buổi học,
    //     môn học" (trước đây chỉ so ngày, lớp có 2 môn học cùng ngày sẽ hiện nhầm bài thi môn kia
    //     lên buổi không liên quan, bạn báo lỗi này);
    // (4) CHÍNH học viên đang xem CHƯA có điểm (hasScore) - đã có điểm rồi thì thôi, không mời làm lại.
    const examByDateSubject = useMemo(() => {
        const items = getResponseItems(gradeDetailsResponse) || []
        const map = {}
        const now = dayjs()
        items.forEach((it) => {
            const grade = it?.grade
            const exam = grade?.course_class_exam
            if (!exam?.id || exam.status !== 1 || !grade?.grade_date) return
            // now.isBefore(...) thay vì so !isSame ngày - dùng !isBefore/isBefore (core dayjs, không
            // cần plugin isSameOrAfter/isSameOrBefore, khớp cách absenceRequestBySessionId ở trên
            // đang né plugin y hệt).
            if (now.isBefore(dayjs(grade.grade_date))) return
            const subjectId = getId(grade.subject)
            if (!subjectId) return
            const displayScore = it.score_type === 'number' ? it.final_score_number : it.final_score_text
            const hasScore = displayScore !== null && displayScore !== undefined && displayScore !== ''
            if (hasScore) return
            const dateKey = dayjs(grade.grade_date).format('YYYY-MM-DD')
            map[`${dateKey}::${subjectId}`] = {grade, exam}
        })
        return map
    }, [gradeDetailsResponse])

    const {presentCount, total} = useMemo(() => {
        const scored = sessions.filter((s) => s.status != null)
        return {
            presentCount: scored.filter((s) => Number(s.status) === 1).length,
            total: scored.length,
        }
    }, [sessions])

    const percent = total > 0 ? Math.round((presentCount / total) * 100) : null

    // Buổi gần nhất CHƯA kết thúc (đang diễn ra hoặc sắp tới) - dùng để tô viền + auto-scroll.
    const upcomingIndex = useMemo(
        () => sessions.findIndex((s) => s?.end_time && !dayjs(s.end_time).isBefore(dayjs())),
        [sessions]
    )

    // Tách buổi ĐÃ diễn ra (gộp vào 1 block tóm tắt) khỏi buổi hiện tại/sắp tới (vẫn hiện đủ card).
    const pastSessions = useMemo(
        () => (upcomingIndex === -1 ? sessions : sessions.slice(0, upcomingIndex)),
        [sessions, upcomingIndex]
    )
    const restSessions = useMemo(
        () => (upcomingIndex === -1 ? [] : sessions.slice(upcomingIndex)),
        [sessions, upcomingIndex]
    )

    useEffect(() => {
        if (!hasScrolledRef.current && upcomingRef.current) {
            upcomingRef.current.scrollIntoView({block: 'center', behavior: 'smooth'})
            hasScrolledRef.current = true
        }
    }, [upcomingIndex])


    if (isLoading) {
        return <div className="h-24 background-100 rounded-xl animate-pulse"/>
    }

    return (
        // Bỏ khung/border/shadow riêng (trước đây tự bọc 1 card trắng độc lập) - component này CHỈ
        // còn được mount lồng bên trong CourseSchedulePanel.js (pane "Sắp tới"), vốn đã có sẵn 1 card
        // ngoài cùng bao trọn cả tiêu đề + nút chuyển view + nội dung - khung riêng ở đây tạo ra "khung
        // trong khung" (bạn báo lỗi này). Không còn nơi nào khác mount component đứng riêng lẻ.
        <>
            <div className="flex flex-col gap-3">
                {total > 0 && (
                    <div className="px-4 py-3 flex items-center gap-2 background-50 rounded-xl">
                        <span className="text-sm text-color-300">
                            {t('attendance rate')}{': '}
                        </span>
                        <span className="font-bold text-sm text-color-000">
                            {presentCount}/{total} {t('sessions')} ({percent}%)
                        </span>
                    </div>
                )}
                {/* Bỏ luôn tiêu đề "Lịch chi tiết" riêng - trùng ý với "Lịch Học Của Lớp" của card
                    ngoài cùng (CourseSchedulePanel.js), bạn báo dup. */}
                {sessions.length > 0 ? (
                    <div className="flex flex-col gap-3">
                        {pastSessions.length > 0 && (
                            <div className="flex flex-col border border-color-100 background shadow-sm hover:shadow-md rounded-2xl overflow-hidden">
                                <PastSessionsSummary
                                    count={pastSessions.length}
                                    totalSessions={sessions.length}
                                    lastStudiedDate={formatSimplifyDate(
                                        pastSessions[pastSessions.length - 1]?.end_time, '', 'DD/MM/YYYY'
                                    )}
                                    expanded={showPastSessions}
                                    onToggle={() => setShowPastSessions((v) => !v)}
                                />
                                <div className="divide-y divide-gray-200">
                                    {showPastSessions && pastSessions.map((s, i) => (
                                        <SessionCard
                                            key={s?.id ?? i}
                                            session={s}
                                            index={i}
                                            isPast={true}
                                            canEdit={can_edit}
                                            canManageAttendance={can_manage_attendance}
                                            showMeetingLink={isOnlineOrHybrid}
                                            privateNote={noteByDetailId[s?.id]}
                                            onRefresh={() => setRefreshKey((k) => k + 1)}
                                            onCreateAttendance={handleCreateAttendance}
                                            onEditAttendance={handleEditAttendance}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {restSessions.length > 0 && (
                            <div className="relative flex items-center py-1">
                                <div className="flex-1 border-t border-dashed border-color-200"/>
                                <span className="px-3 flex items-center gap-1.5 text-2xs font-bold text-blue-600 uppercase tracking-wider whitespace-no-wrap">
                                    <BsLightningChargeFill size={10}/>
                                    {t('next upcoming session title')}
                                </span>
                                <div className="flex-1 border-t border-dashed border-color-200"/>
                            </div>
                        )}
                        {restSessions.map((s, i) => {
                            const globalIndex = pastSessions.length + i
                            return (
                                <SessionCard
                                    key={s?.id ?? globalIndex}
                                    ref={i === 0 ? upcomingRef : null}
                                    session={s}
                                    index={globalIndex}
                                    isUpcoming={i === 0}
                                    isPast={false}
                                    canEdit={can_edit}
                                    canManageAttendance={can_manage_attendance}
                                    showMeetingLink={isOnlineOrHybrid}
                                    privateNote={noteByDetailId[s?.id]}
                                    onRefresh={() => setRefreshKey((k) => k + 1)}
                                    onCreateAttendance={handleCreateAttendance}
                                    onEditAttendance={handleEditAttendance}
                                    canRequestAbsence={!!myStudentId}
                                    absenceRequest={absenceRequestBySessionId[s?.id]}
                                    onRequestAbsence={handleRequestAbsence}
                                    onCancelAbsence={handleCancelAbsence}
                                    examInfo={(() => {
                                        const subjectId = getId(s?.subject)
                                        if (!s?.start_time || !subjectId) return undefined
                                        return examByDateSubject[`${dayjs(s.start_time).format('YYYY-MM-DD')}::${subjectId}`]
                                    })()}
                                />
                            )
                        })}
                    </div>
                ) : (
                    <EmptyHolder style={{border: 'none'}}/>
                )}
            </div>
            {attendanceModal}
            {absenceModal}
        </>
    )
}

export default MyClassSessionsPanel
